import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useMemo } from 'react';
import { EventNameDisplay } from 'src/components/Events/EventInfo';
import {
  isCampaignActive,
  UpdateCampaignStateButton,
} from 'src/components/SponsoredListings';
import { BoostListingButton } from 'src/components/SponsoredListings/BoostListingButton';
import { BaseCell } from 'src/components/TableV2/cells';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext/EventMapContext';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { createInventoryEventUrl } from 'src/navigations/Routes/InventoryEvent/InventoryEvent.utils';
import { ChevronDownIconV2 } from 'src/svgs/Viagogo/goicons/ChevronDownIconV2';
import { ChevronUpIconV2 } from 'src/svgs/Viagogo/goicons/ChevronUpIconV2';
import { TableShimmeringDiv } from 'src/tables/Table';
import { MOCK_SPONSORED_LISTINGS_DATA } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import {
  applyNumberFormatting,
  formatPercent,
} from 'src/utils/numberFormatter';
import {
  AdCampaignState,
  BiddingMode,
  TicketClassInfo,
} from 'src/WebApiController';

import { BaseBidInput } from '../BaseBidInput';
import * as styles from '../SponsoredListingsTable.css';
import { FlightTableRow } from './CampaignFlightsTable';

export const getTableColumnDefinitions = (
  onStartWork?: () => void,
  onStopWork?: (success: boolean) => void,
  disabled?: boolean
): ColumnDef<FlightTableRow>[] => [
  {
    id: 'startDate',
    size: 150,
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.FlightStartDateTime} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) =>
      new Date(data.flight?.dateRange?.start ?? data.name).toISOString(),
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }
      const value = getValue<string | undefined>();
      if (!value) {
        return <BaseCell align="right" />;
      }

      return (
        <BaseCell align="right">
          {original.flight ? (
            formatInTimeZone(new Date(value), 'UTC', 'M/d/yy, h:mm a')
          ) : (
            <b>{formatInTimeZone(new Date(value), 'UTC', 'MM/dd/yyyy')}</b>
          )}
        </BaseCell>
      );
    },
  },
  {
    id: 'duration',
    size: 70,
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Duration} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) =>
      data.flight?.dateRange?.end && data.flight?.dateRange?.start
        ? new Date(data.flight?.dateRange?.end).getTime() -
          new Date(data.flight?.dateRange?.start).getTime()
        : null,
    cell: function Cell({ getValue, row: { original } }) {
      // Convert milliseconds to hours
      const durationText = useMemo(() => {
        const value = getValue<number | undefined>();
        if (!value) {
          return null;
        }

        const durationHours = value / (1000 * 60 * 60);

        let durationText = '';
        if (durationHours >= 0 && durationHours < 25) {
          durationText = `${Math.round(durationHours)}h`;
        } else if (durationHours >= 25) {
          const days = Math.floor(durationHours / 24);
          const hours = Math.round(durationHours % 24);
          durationText = hours > 0 ? `${days}d ${hours}h` : `${days}d`;
        }

        return durationText;
      }, [getValue]);

      if (!original) {
        return <TableShimmeringDiv />;
      }
      if (!durationText) {
        return <BaseCell align="right" />;
      }

      return <BaseCell align="right">{durationText}</BaseCell>;
    },
  },
  {
    id: 'eventName',
    size: 250,
    header: () => (
      <TableHeaderCellDiv align="left">
        <Content id={ContentId.Event} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.eventName,
    cell: function Cell({ getValue, row: { original } }) {
      const eventName = getValue<string | undefined>();

      if (!original) {
        return <TableShimmeringDiv />;
      }

      if (!original.eventNameParts) {
        return null;
      }

      return (
        <BaseCell align="left">
          <Button
            variant="text"
            title={eventName}
            onClick={(e) => {
              if (original.eventId) {
                window.open(
                  createInventoryEventUrl(original.eventId.toString(), true),
                  '_blank',
                  'noopener noreferrer'
                );
              }
            }}
          >
            <EventNameDisplay
              eventName={original.eventNameParts[0]}
              eventNameConnector={original.eventNameParts[1]}
              eventSubName={original.eventNameParts[2]}
            />
          </Button>
        </BaseCell>
      );
    },
  },
  {
    id: 'eventDate',
    size: 150,
    header: () => (
      <TableHeaderCellDiv align="left">
        <Content id={ContentId.EventDate} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.eventDate,
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = getValue<Date | undefined>();
      if (!value) {
        return null;
      }

      const eventDate = format(value, 'MMM d - h:mm a');

      return (
        <BaseCell>
          <div>{eventDate}</div>
        </BaseCell>
      );
    },
  },
  {
    id: 'campaign',
    size: 250,
    header: () => (
      <TableHeaderCellDiv align="left">
        <Content id={ContentId.CampaignName} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.campaign?.campaignName,
    sortingFn: 'textCaseSensitive',
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }
      const value = getValue<string>();
      if (!value) {
        return null;
      }

      return (
        <BaseCell align="left">
          <Stack gap="m" direction="row" alignItems="center">
            <span>{value}</span>
            {!!original.campaign && !isCampaignActive(original.campaign) && (
              <div className={styles.pausedBadge}>
                <Content id={ContentId.Paused} />
              </div>
            )}
          </Stack>
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      return (
        <BaseCell align="left" style={{ fontWeight: 'bolder' }}>
          <Content id={ContentId.FlightsTotal} />
        </BaseCell>
      );
    },
  },
  {
    id: 'revenue',
    size: 100,
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Revenue} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      const salesData = data.salesData;
      let revenue = salesData?.totalNetProceeds;

      if (data.flights) {
        // For aggregated flights, sum up revenue from all flights
        revenue = data.flights.reduce((sum, flight) => {
          const flightData = flight.salesData;
          return sum + (flightData?.totalNetProceeds || 0);
        }, 0);
      }

      if (MOCK_SPONSORED_LISTINGS_DATA && !revenue) {
        // Generate a random revenue between $100 and $10,000
        revenue = Math.floor(Math.random() * 9900) + 100;
      }

      return revenue;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const revenue = getValue<number>();

      return (
        <BaseCell align="right">
          {!!revenue && (
            <div
              className={revenue > 0 ? styles.revenuePositiveCell : undefined}
            >
              {applyNumberFormatting({
                inputNumber: revenue,
                currencyCode: original.currency?.code,
                currencyDecimalDigits: original.currency?.dec,
              })}
            </div>
          )}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return null;
      }

      const value = accessorFn?.(original, -1) as number | null;

      if (value == null) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          {!!original.salesData?.totalNetProceeds &&
            applyNumberFormatting({
              inputNumber: original.salesData.totalNetProceeds,
              currencyCode: original.currency?.code,
              currencyDecimalDigits: original.currency?.dec,
            })}
        </BaseCell>
      );
    },
  },
  {
    id: 'spend',
    size: 100,
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.AdSpend} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      if (data.flights) {
        // For aggregated flights, sum up spend from all flights
        return data.flights.reduce(
          (sum, flight) => sum + (flight.flight?.flightSpend || 0),
          0
        );
      }
      return data.flight?.flightSpend;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const spend = getValue<number | undefined>();

      return (
        <BaseCell align="right">
          {!!spend &&
            applyNumberFormatting({
              inputNumber: spend,
              currencyCode: original.currency?.code,
              currencyDecimalDigits: original.currency?.dec,
            })}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          {!!original.flight?.flightSpend &&
            applyNumberFormatting({
              inputNumber: original.flight.flightSpend,
              currencyCode: original.currency?.code,
              currencyDecimalDigits: original.currency?.dec,
            })}
        </BaseCell>
      );
    },
  },
  {
    id: 'roas',
    size: 100,
    header: () => <TableHeaderCellDiv align="right">ROAS</TableHeaderCellDiv>,
    accessorFn: (data) => {
      const salesData = data.salesData;

      let revenue = salesData?.totalNetProceeds ?? 0;
      let spend = data.flight?.flightSpend ?? 0;

      if (data.flights) {
        // For aggregated flights, sum up revenue and spend from all flights
        revenue = data.flights.reduce((sum, flight) => {
          const flightData = flight.salesData;
          return sum + (flightData?.totalNetProceeds || 0);
        }, 0);
        spend = data.flights.reduce(
          (sum, flight) => sum + (flight.flight?.flightSpend || 0),
          0
        );
      }

      // In development, assign random revenue if the actual value is 0
      if (MOCK_SPONSORED_LISTINGS_DATA && !revenue) {
        // Generate a random revenue between $1 and budget * 10
        revenue =
          Math.floor(
            Math.random() * ((data?.flight?.flightBudget ?? 1) * 10 - 1)
          ) + 1;
      }

      const roas = spend ? revenue / spend : 0;

      return roas;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const roas = getValue<number>();
      return <BaseCell align="right">{!!roas && formatPercent(roas)}</BaseCell>;
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;
      if (!original) {
        return null;
      }

      const totalRevenue = original.salesData?.totalNetProceeds ?? 0;
      const totalSpend = original.flight?.flightSpend;

      const totalRoas = totalSpend ? totalRevenue / totalSpend : 0;

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          <Stack direction="column">
            <span>{!!totalRevenue && formatPercent(totalRoas, 2)}</span>
          </Stack>
        </BaseCell>
      );
    },
  },
  {
    id: 'sales-count',
    size: 100,
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Sales} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      const salesData = data.salesData;
      let saleCount = salesData?.saleCount ?? 0;

      if (data.flights) {
        // For aggregated flights, sum up sale counts from all flights
        saleCount = data.flights.reduce((sum, flight) => {
          const flightData = flight.salesData;
          return sum + (flightData?.saleCount || 0);
        }, 0);
      }

      return saleCount;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const saleCount = getValue<number>();

      return <BaseCell align="right">{saleCount}</BaseCell>;
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          <span>{original.salesData?.saleCount || ' '}</span>
        </BaseCell>
      );
    },
  },
  {
    id: 'cpc',
    size: 150,
    header: () => <TableHeaderCellDiv align="right">CPC</TableHeaderCellDiv>,
    accessorFn: (data) => data.flight?.baseBid,
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const cpc = getValue<number | null>();

      return (
        <BaseCell align="right">
          {original.campaign?.biddingMode === BiddingMode.Manual ? (
            original.campaign && original.flight?.isCurrent ? (
              <BaseBidInput currencyCode={original.currency?.code ?? 'USD'} />
            ) : (
              applyNumberFormatting({
                inputNumber: cpc,
                currencyCode: original.currency?.code,
                currencyDecimalDigits: original.currency?.dec,
              })
            )
          ) : null}
        </BaseCell>
      );
    },
  },
  {
    id: 'budget',
    size: 100,
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Budget} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      if (data.flights) {
        // For aggregated flights, sum up budget from all flights
        return data.flights.reduce(
          (sum, flight) => sum + (flight.flight?.flightBudget || 0),
          0
        );
      }
      return data.flight?.flightBudget;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const budget = getValue() as number | null;

      return (
        <BaseCell align="right">
          {!!budget &&
            applyNumberFormatting({
              inputNumber: budget,
              currencyCode: original.currency?.code,
              currencyDecimalDigits: original.currency?.dec,
            })}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          {!!original.flight?.flightBudget &&
            applyNumberFormatting({
              inputNumber: original.flight.flightBudget,
              currencyCode: original.currency?.code,
              currencyDecimalDigits: original.currency?.dec,
            })}
        </BaseCell>
      );
    },
  },
  {
    id: 'budget-remaining',
    size: 100,
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Remaining} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      let budget: number;
      let spend: number;
      if (data.flights) {
        // For aggregated flights, sum up budget and spend from all flights
        budget = data.flights.reduce(
          (sum, flight) => sum + (flight.flight?.flightBudget || 0),
          0
        );
        spend = data.flights.reduce(
          (sum, flight) => sum + (flight.flight?.flightSpend || 0),
          0
        );
      } else {
        // Calculate budget left for individual campaign or flight
        budget = data.flight?.flightBudget ?? 0;
        spend = data.flight?.flightSpend ?? 0;
      }

      const remaining = budget - spend;

      return remaining;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const remaining = getValue<number>();

      return (
        <BaseCell align="right">
          {!!remaining &&
            applyNumberFormatting({
              inputNumber: remaining,
              currencyCode: original.currency?.code,
              currencyDecimalDigits: original.currency?.dec,
            })}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          {(!!original.flight?.flightBudget ||
            !!original.flight?.flightSpend) &&
            applyNumberFormatting({
              inputNumber:
                (original.flight.flightBudget ?? 0) -
                (original.flight.flightSpend ?? 0),
              currencyCode: original.currency?.code,
              currencyDecimalDigits: original.currency?.dec,
            })}
        </BaseCell>
      );
    },
  },
  {
    id: 'target',
    size: 150,
    header: () => (
      <TableHeaderCellDiv align="left">
        <Content id={ContentId.Target} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.campaign?.targetTicketClassIds,
    enableSorting: false,
    cell: function Cell({ getValue, row: { original } }) {
      const { venueMapInfo } = useEventMapContext();
      const allZoneTxt = useContent(ContentId.AllZones);

      // Extract all TicketClassInfo from venueMapInfo as a record keyed by ticketClassId
      // Extract all TicketClassInfo from venueMapInfo as a record keyed by ticketClassId
      const ticketClassesById = useMemo(() => {
        if (!venueMapInfo) return {};

        return venueMapInfo.sections
          .flatMap((s) => s.rows.map((r) => r.tktClass))
          .reduce(
            (record, ticketClass) => {
              if (ticketClass?.id) {
                record[ticketClass.id] = ticketClass;
              }
              return record;
            },
            {} as Record<number, TicketClassInfo>
          );
      }, [venueMapInfo]);

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const tktClassIds = getValue<number[] | undefined>();

      // Display target information
      let target = allZoneTxt;
      if (tktClassIds?.length && Object.keys(ticketClassesById).length > 0) {
        const targetNames = tktClassIds
          .map((id) => ticketClassesById[id]?.name)
          .filter(Boolean)
          .join(', ');
        target = targetNames || target;
      }

      return <BaseCell>{target}</BaseCell>;
    },
  },
  {
    id: 'actions',
    size: 100,
    header: () => <TableHeaderCellDiv align="center"></TableHeaderCellDiv>,
    accessorFn: (data) => data.flight,
    cell: function Cell({
      getValue,
      row: { getCanExpand, getIsExpanded, toggleExpanded, original },
    }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const canExpand = getCanExpand();
      const isExpanded = getIsExpanded();
      const Icon = isExpanded ? ChevronUpIconV2 : ChevronDownIconV2;
      const campaign = original.campaign;

      return (
        <BaseCell
          align="center"
          style={{ padding: vars.spacing.sm, paddingRight: vars.spacing.m }}
        >
          <Stack gap="m" alignItems="center" justifyContent="center">
            {campaign && (
              <>
                <UpdateCampaignStateButton
                  iconOnly
                  campaign={campaign}
                  state={
                    isCampaignActive(campaign)
                      ? AdCampaignState.Paused
                      : AdCampaignState.Active
                  }
                  onBeforePause={onStartWork}
                  onAfterPause={onStopWork}
                  disabled={disabled}
                />
                <BoostListingButton
                  campaign={campaign}
                  iconOnly
                  disabled={disabled}
                />
              </>
            )}
            {canExpand && (
              <Icon
                size={vars.iconSize.s}
                withHoverEffect
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  toggleExpanded(!isExpanded);
                }}
              />
            )}
          </Stack>
        </BaseCell>
      );
    },
  },
];
